import React, { useState } from 'react';
import { isIosNativeCheckout, restorePremiumViaIap, diagnoseStoreKitApis } from '../utils/checkout';

/**
 * App Store ガイドライン 3.1.1 が要求する「購入を復元」ボタン。
 *
 * 要件:
 *  - 起動時の自動復元ではなく、ユーザーが押す独立したボタンであること
 *  - 購入画面と設定画面の両方から、ログイン状態・購読状態を問わず見つかること
 * そのため表示条件は「iOS ネイティブかどうか」だけにしている。
 *
 * @param {'card'|'inline'} variant  card = 見出し付きカード（設定画面）/ inline = ボタンのみ（課金画面）
 * @param {() => Promise<void>|void} onRestored  復元成功後に呼ばれる（ユーザー情報の再取得など）
 */
export default function RestorePurchasesPanel({ variant = 'card', onRestored }) {
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');

  if (!isIosNativeCheckout()) return null;

  const handleRestore = async () => {
    setOkMsg('');
    setErrMsg('');
    setBusy(true);
    try {
      await restorePremiumViaIap();
      if (onRestored) await onRestored();
      setOkMsg('購入を復元しました。プレミアムが有効です。');
    } catch (e) {
      if (e?.cancelled) return;
      setErrMsg(e?.response?.data?.error || e?.message || '購入の復元に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <button data-control="secondary" data-control-id="RestorePurchasesPanel-btn"
      type="button"
      className="rp-btn"
      style={{ ...S.btn, ...(busy ? S.btnBusy : {}) }}
      onClick={handleRestore}
      disabled={busy}
    >
      {busy ? '復元中…' : '購入を復元'}
    </button>
  );

  const messages = (
    <>
      <button data-control="secondary" data-control-id="RestorePurchasesPanel-btn" type="button" style={{ ...S.btn, marginTop: 8, fontSize: 11 }}
        onClick={async () => setOkMsg(await diagnoseStoreKitApis())}>診断</button>
      {okMsg ? <p className="rp-msg" style={{ ...S.ok, fontSize: 11, wordBreak: 'break-all' }}>{okMsg}</p> : null}
      {errMsg ? <p className="rp-shake" style={S.err}>{errMsg}</p> : null}
    </>
  );

  if (variant === 'inline') {
    return (
      <div style={S.inlineWrap}>
        <style>{CSS}</style>
        {button}
        <p style={S.inlineHint}>
          機種変更や再インストールで購読が消えたときは、購入時と同じ Apple ID でサインインしてタップしてください。
        </p>
        {messages}
      </div>
    );
  }

  return (
    <div style={S.card}>
      <style>{CSS}</style>
      <div style={S.head}>
        <span style={S.badge} aria-hidden>↺</span>
        <div>
          <h3 style={S.title}>購入を復元</h3>
          <p style={S.desc}>
            機種変更・再インストール・再ログインの後にプレミアムが消えている場合は、
            購入時と同じ Apple ID でサインインしたうえでタップしてください。追加の料金はかかりません。
          </p>
        </div>
      </div>
      {button}
      {messages}
    </div>
  );
}

const CSS = `
.rp-btn{transition:transform .25s cubic-bezier(0.34,1.56,0.64,1),box-shadow .25s ease}
.rp-btn:not(:disabled):hover{transform:translateY(-2px);box-shadow:0 10px 24px rgba(16,185,129,0.32)}
.rp-btn:not(:disabled):active{transform:translateY(0)}
.rp-msg{animation:bounceIn .45s cubic-bezier(0.34,1.56,0.64,1)}
.rp-shake{animation:shake .4s ease}
@media (prefers-reduced-motion: reduce){
  .rp-btn,.rp-msg,.rp-shake{transition:none;animation:none}
  .rp-btn:not(:disabled):hover{transform:none}
}
`;

const S = {
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border-light)',
    borderRadius: 'var(--r-card)',
    padding: '18px 18px 20px',
    boxShadow: 'var(--shadow-sm)',
  },
  head: { display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 },
  badge: {
    flexShrink: 0,
    width: 38, height: 38,
    borderRadius: 13,
    background: 'var(--primary-ultra)',
    color: 'var(--primary-dark)',
    display: 'grid', placeItems: 'center',
    fontSize: 19, fontWeight: 800,
  },
  title: { fontSize: 15, fontWeight: 800, color: 'var(--text)', margin: '2px 0 4px' },
  desc: { fontSize: 12.5, lineHeight: 1.65, color: 'var(--text-secondary)', margin: 0 },

  btn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '13px 16px',
    borderRadius: 'var(--r-btn)',
    border: '1.5px solid var(--primary)',
    background: 'var(--surface)',
    color: 'var(--primary-dark)',
    fontSize: 14.5,
    fontWeight: 800,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(16,185,129,0.18)',
  },
  btnBusy: { opacity: 0.6, cursor: 'not-allowed' },
  btnIcon: { fontSize: 16, lineHeight: 1 },

  inlineWrap: { marginTop: 12 },
  inlineHint: {
    fontSize: 11.5,
    lineHeight: 1.6,
    color: 'var(--text-muted)',
    margin: '8px 2px 0',
    textAlign: 'left',
  },

  ok: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--primary-dark)',
    background: 'var(--primary-ultra)',
    borderRadius: 12,
    padding: '10px 12px',
    margin: '12px 0 0',
  },
  err: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--error)',
    background: 'var(--error-light)',
    borderRadius: 12,
    padding: '10px 12px',
    margin: '12px 0 0',
    lineHeight: 1.6,
  },
};
